import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilValue, useResetRecoilState } from 'recoil';
import { DateTime } from 'luxon';

import { FilterPopup } from '@storybook';
import { DatePicker } from 'components';
import { API_URL } from 'constant';
import { filteringTagsState, SelectedActivityDateRangeState } from 'global-stores';
import { useNetwork, useOutsideClick, useTimezoneDate } from 'hooks';
import { getOffDate } from 'utils';
import { Drawer, DrawerHead, SearchBox } from 'views/design-system';

interface IActivityLog {
	_id: string;
	createdAt: string;
	userName: string;
	action: string;
	profile?: string;
}

interface IActivityDrawer {
	open: boolean;
	flow: { _id: string; name: string } | null;
	onClose: () => void;
}

/**
 * The onboarding flow's activity log, as a drawer instead of a full-page
 * takeover — so the list it belongs to stays on screen behind it.
 *
 * Same endpoint and same params as the takeover it replaces, including the
 * editor filter (`&userIds=` per checked user) and the direct page-number
 * input, which is the only way to reach page 30 of an old flow's history.
 */
export const ActivityDrawer: FC<IActivityDrawer> = ({ open, flow, onClose }) => {
	const [loading, setLoading] = useState(true);
	const [searchTerm, setSearchTerm] = useState('');
	const [showEditorFilter, setShowEditorFilter] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [pageInput, setPageInput] = useState('1');
	const [data, setData] = useState<{ groups: Record<string, IActivityLog[]>; totalPages: number }>({
		groups: {},
		totalPages: 1,
	});

	const editorRef = useRef<HTMLDivElement>(null);
	const didMount = useRef(false);

	const dateRange = useRecoilValue(SelectedActivityDateRangeState);
	const filteringTags = useRecoilValue(filteringTagsState);
	const resetFilteringTags = useResetRecoilState(filteringTagsState);

	const { get } = useNetwork({ updateState: false });
	const { formatDate: formatWithZone } = useTimezoneDate();

	useOutsideClick(editorRef, () => setShowEditorFilter(false));

	const groupByDate = useCallback((list: IActivityLog[] = []) => {
		return list.reduce((acc: Record<string, IActivityLog[]>, item) => {
			const date = item.createdAt?.split('T')[0] || '';
			if (!acc[date]) acc[date] = [];
			acc[date]?.push(item);
			return acc;
		}, {});
	}, []);

	const load = useCallback(async () => {
		if (!flow?._id) return;
		setLoading(true);
		const params: Record<string, string> = {
			type: 'activities',
			page: String(currentPage),
			limit: '10',
			startDate: getOffDate(dateRange?.[0]?.startDate || '', 'start'),
			endDate: getOffDate(dateRange?.[0]?.endDate || '', 'end'),
		};
		if (searchTerm) params.nameRegex = searchTerm;
		const userIds = (filteringTags.editor ?? [])
			.map((id: string) => `&userIds=${id}`)
			.join('');
		const resp = await get(
			`${API_URL.COMPLEX_FLOW}/${flow._id}?${new URLSearchParams(params).toString()}${userIds}`
		).catch(() => null);
		if (resp?.message === 'ok') {
			setData({
				groups: groupByDate(resp.data?.activities),
				totalPages: resp.data?.totalPages ?? 1,
			});
		}
		setLoading(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [flow?._id, currentPage, dateRange, searchTerm, filteringTags.editor]);

	/** "Today at 3:04 PM" / "Yesterday at …" / "08/17/2026 at …" */
	const stampOf = useCallback(
		(value: string) => {
			try {
				const now = DateTime.now();
				const at = DateTime.fromJSDate(new Date(value));
				const time = formatWithZone(at.toJSDate(), 'hh:mm a');
				if (at.hasSame(now, 'day')) return `Today at ${time}`;
				if (at.hasSame(now.minus({ days: 1 }), 'day')) return `Yesterday at ${time}`;
				return `${formatWithZone(at.toJSDate(), 'MM/dd/yyyy', true)} at ${time}`;
			} catch {
				return 'Invalid Date';
			}
		},
		[formatWithZone]
	);

	const paginate = useCallback(
		(direction: 'next' | 'prev') => {
			setCurrentPage(prev => {
				if (direction === 'next' && prev < data.totalPages) return prev + 1;
				if (direction === 'prev' && prev > 1) return prev - 1;
				return prev;
			});
		},
		[data.totalPages]
	);

	useEffect(() => setPageInput(String(currentPage)), [currentPage]);

	// The typed page number settles before it becomes a request.
	useEffect(() => {
		const id = window.setTimeout(() => {
			const parsed = parseInt(pageInput, 10);
			if (!Number.isNaN(parsed) && parsed >= 1 && parsed <= data.totalPages && parsed !== currentPage) {
				setCurrentPage(parsed);
			}
		}, 500);
		return () => window.clearTimeout(id);
	}, [pageInput, data.totalPages, currentPage]);

	// A new search/filter/date starts at page 1 rather than on whatever page the
	// previous query happened to be showing.
	useEffect(() => {
		if (!didMount.current) return undefined;
		if (currentPage !== 1) {
			setCurrentPage(1);
			return undefined;
		}
		const id = window.setTimeout(load, 300);
		return () => window.clearTimeout(id);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchTerm, dateRange, filteringTags?.editor]);

	useEffect(() => {
		if (!open || !flow?._id) return undefined;
		const id = window.setTimeout(load, 300);
		return () => window.clearTimeout(id);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentPage, flow?._id, open]);

	useEffect(() => {
		didMount.current = true;
		return () => resetFilteringTags();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const groups = Object.entries(data.groups);

	return (
		<Drawer open={open} label="Onboarding flow activity">
			<DrawerHead title={flow?.name || 'Activity log'} onClose={onClose}>
				<div className="dsteps">Activity log</div>
				<div className="dbtns">
					<SearchBox value={searchTerm} onChange={setSearchTerm} title="Searches the editor's name" />
					<div ref={editorRef} style={{ position: 'relative' }}>
						<button className="dbtn" type="button" onClick={() => setShowEditorFilter(v => !v)}>
							Editors &#9662;
						</button>
						{showEditorFilter && <FilterPopup filterPage="activity-logs" />}
					</div>
					<DatePicker stateType={SelectedActivityDateRangeState} />
					<span className="pgn">
						<button
							type="button"
							aria-label="Previous page"
							disabled={currentPage === 1}
							onClick={() => paginate('prev')}
						>
							&#8249;
						</button>
						<input
							className="pgin"
							aria-label="Page number"
							value={pageInput}
							onChange={e => {
								const raw = e.target.value.replace(/\D/g, '');
								if (raw === '' || +raw <= data.totalPages) setPageInput(raw);
							}}
						/>
						<span className="pg">of {data.totalPages}</span>
						<button
							type="button"
							aria-label="Next page"
							disabled={currentPage === data.totalPages}
							onClick={() => paginate('next')}
						>
							&#8250;
						</button>
					</span>
				</div>
			</DrawerHead>

			{loading && <div className="sv2-loading">Loading activity…</div>}

			{!loading &&
				groups.map(([date, logs]) => (
					<div key={date}>
						<div className="grp">{formatWithZone(date, 'EEEE, MMMM dd, yyyy')}</div>
						{logs.map(log => (
							<div className="act" key={log._id}>
								<b>{log.userName}</b> <b>{log.action}</b> the onboarding flow.
								<span className="at">{stampOf(log.createdAt)}</span>
							</div>
						))}
					</div>
				))}

			{!loading && !groups.length && <div className="sv2-empty">No Record Found!</div>}
		</Drawer>
	);
};
